import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { sendOTPOnEmail, verifyOTP } from '../../services/auth';
import { ResponseCodes } from '../../types/responseCodes';
import { ShowAndHideOtp } from './ShowAndHideOtp';
import { AuthContainer } from './AuthContainer';
import { useUserContext } from "../../context/user";
import { maskEmail } from "../../helpers/text";
import { sendPushToken } from "../../services/notifications";
import { getFirebaseToken } from "../../firebase";
import { AuthType } from '../../types/auth';
import { useTimer } from "../../hooks/useTimer"

const wrong_otp_error = "Введен неверный OTP код";
const otp_error = "Произошла ошибка при отправке OTP кода";
const required_field = "*обязательное поле";
const attempts_limit_ended = "Необходимо запросить код заново";
const attempts_msg = (num: number) => {
    return num > 0 ? `Осталось попыток: ${num}` : attempts_limit_ended
};

export const OTPInput = ({ phone, email }: { phone: string, email?: string }) => {
    const { setRole, setJwtToken, setJwtRefreshToken, setUserAuthType } = useUserContext();
    const [verificationStatus, setVerificationStatus] = useState('idle');
    const [errorMessage, setErrorMessage] = useState("");
    const [password, setPassword] = useState("");
    const [attempts, setAttempts] = useState(0);
    const { minutes, seconds, startTimer, stopTimer, resetTimer, isRunning: isTimerRunning } = useTimer({ initialMinutes: 5, initialSeconds: 0 });

    const numInputs = 6;
    const maxAttempts = 3;

    useEffect(() => {
        if (attempts >= maxAttempts) {
            window.location.replace("/sign_in/phone");
        }
    }, [attempts]);

    const { mutate: fcmToken } = useMutation({
        mutationFn: (data: { token: string, jwt: string }) => sendPushToken(data.token, data.jwt),
        onSettled: (data) => {
            // Ставим статус верификации, после отправки запроса
            setVerificationStatus('success');
        }
    });

    useEffect(() => {
        resetTimer()
        startTimer()
    }, []);

    useEffect(() => {
        setErrorMessage("");
    }, [password]);

    const { mutate: OTP } = useMutation({
        mutationFn: verifyOTP,
        onSuccess: (response) => {
            setJwtToken(response.access);
            setJwtRefreshToken(response.refresh);
            setRole(response.role);
            // Запрашиваем токен
            getFirebaseToken().then(token => {
                // Если токен есть, то отправляем его на бэк
                if (token) {
                    fcmToken({ token, jwt: response.access });
                } else {
                    // Ставим статус верификации, если нет токена
                    setVerificationStatus('success');
                }
            })
        },
        onError: (error: any) => {
            if (error.status === ResponseCodes.FORBIDDEN) {
                incrementAttempts()
                setErrorMessage(wrong_otp_error);
            } else {
                setErrorMessage(otp_error);
            }
            setVerificationStatus('error');
        },
    });

    const { mutate: getOTP } = useMutation({
        mutationFn: sendOTPOnEmail,
        onSuccess: (data) => {
            console.log("New OTP sent");
            //resetAttempts()
            resetTimer()
            startTimer()
        }
    });

    const submit = () => {
        if (password.length === 0) {
            setErrorMessage(required_field);
        }
        if (password.length === numInputs && verificationStatus !== 'success') {
            OTP({ phone, otp_code: password });
        }
    }

    // Отслеживаем статус верификации
    useEffect(() => {
        if (verificationStatus === 'success') {
            stopTimer()
            // Сохраняем тип авторизации (для пин кода, т.к пин только для вошедших по номеру телефона)
            setUserAuthType(AuthType.PHONE)
            // После успешной верификации отправляем либо на создание пин кода или на его ввод
            window.location.replace('/sign_in/pin');
        }
    }, [verificationStatus]);


    const onChange = (value: string) => {
        if (value.length <= numInputs) {
            setPassword(value)
        }
    }

    const retryOTP = () => {
        getOTP({ phone });
        setErrorMessage('');
        setPassword("");
    }

    const incrementAttempts = () => {
        setAttempts(attempts + 1)
    }

    const resetAttempts = () => {
        setAttempts(0);
    }

    const attemptsText = () => {
        if (attempts > 0) {
            return (
                '\n' + attempts_msg(maxAttempts - attempts)
            )
        }
        return null
    }

    const timerComponent = () => {
        if (isTimerRunning) {
            return (
                <div className="
                    mt-5 font-manager-acrom-h-24-medium-acrom 
                    font-[number:var(--manager-acrom-h4-24-medium-acrom-font-weight)]
                    text-black text-[length:var(--manager-acrom-h4-24-medium-acrom-font-size)]
                    text-center tracking-[var(--manager-acrom-h4-24-medium-acrom-letter-spacing)] 
                    leading-[var(--manager-acrom-h4-24-medium-acrom-line-height)]
                    [font-style:var(--manager-acrom-h4-24-medium-acrom-font-style)]
                 ">
                    {minutes} : {seconds}
                </div>
            )
        }

        return (
            <button
                onClick={retryOTP}
                className='mt-4 text-center flex items-center justify-center text-gray-500 underline cursor-pointer'>
                Отправить OTP-код заново
            </button>
        )
    };

    return (
        <AuthContainer>
            <div className="w-full">
                <div
                    className="mb-8 font-manager-acrom-h4-24-medium-acrom font-[number:var(--manager-acrom-h4-24-medium-acrom-font-weight)] text-black text-[length:var(--manager-acrom-h4-24-medium-acrom-font-size)] text-center tracking-[var(--manager-acrom-h4-24-medium-acrom-letter-spacing)] leading-[var(--manager-acrom-h4-24-medium-acrom-line-height)] [font-style:var(--manager-acrom-h4-24-medium-acrom-font-style)]">
                    Введите проверочный код
                </div>
                <div
                    className='w-full mb-8 text-center flex flex-col items-center justify-center mt-4 text-sm'>
                    <div className="text-gray-400">
                        Код был направлен на почту
                    </div>
                    <div className="text-gray-800">
                        {email ? maskEmail(email) : phone}
                    </div>
                </div>
                <ShowAndHideOtp
                    password={password}
                    setPassword={onChange}
                    containerStyles={errorMessage === required_field ? "border-2 border-red-300" : ""}
                />

                {errorMessage !== "" ? <span
                    className='text-center flex items-center justify-center text-red-500 text-sm mt-1 mb-2'
                    style={{ whiteSpace: 'pre-line' }}>{errorMessage + attemptsText()}
                </span> : ""}
                <div className="w-full flex flex-col justify-center items-center">
                    <button
                        onClick={submit}
                        className={`
                        w-full h-12 
                        inline-flex 
                        items-center 
                        justify-center flex-[0_0_auto] 
                        rounded-lg gap-2.5 px-5 py-4 relative 
                        font-manager-acrom-ph1-18-medium-acrom 
                        font-[number:var(--manager-acrom-ph1-18-medium-acrom-font-weight)] 
                        text-[#979797] 
                        text-[length:var(--manager-acrom-ph1-18-medium-acrom-font-size)] 
                        text-center 
                        tracking-[var(--manager-acrom-ph1-18-medium-acrom-letter-spacing)] 
                        leading-[var(--manager-acrom-ph1-18-medium-acrom-line-height)] 
                        [font-style:var(--manager-acrom-ph1-18-medium-acrom-font-style)]
                        ${password.length === numInputs ? "bg-melon-yellow text-melon-black" : "bg-melon-light-grey"}
                        `}
                    >
                        Далее
                    </button>
                    {timerComponent()}
                    <a
                        href="mailto:report@melonfashion.com"
                        className="mt-10 font-manager-acrom-links font-[number:var(--manager-acrom-links-font-weight)] text-black text-[length:var(--manager-acrom-links-font-size)] text-center tracking-[var(--manager-acrom-links-letter-spacing)] leading-[var(--manager-acrom-links-line-height)] underline whitespace-nowrap [font-style:var(--manager-acrom-links-font-style)]"
                    >Связаться с тех. поддержкой</a>
                </div>
            </div>
        </AuthContainer>
    );
};